<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog financial-transfer-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="financial-transfer-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="financial-transfer-modal-title">{{ isEditMode ? text.modal.edit.title : text.modal.create.title }}</h3>
          <p>{{ isEditMode ? text.modal.edit.subtitle : text.modal.create.subtitle }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.modal.closeAriaLabel"
          :disabled="loading"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__content financial-transfer-modal__content">
        <form class="app-modal__form" @submit.prevent="submitForm">
          <section class="app-modal__section">
            <div class="app-modal__grid two-columns">
              <div class="app-modal-field">
                <span>{{ text.modal.fields.sourceAccount }}</span>
                <multiselect
                  ref="sourceSelect"
                  v-model="form.sourceAccountOption"
                  :options="sourceAccountOptions"
                  track-by="value"
                  label="label"
                  :multiple="false"
                  :close-on-select="true"
                  :clear-on-select="false"
                  :preserve-search="false"
                  :show-labels="false"
                  :searchable="true"
                  :allow-empty="false"
                  :max-height="220"
                  :placeholder="text.modal.placeholders.unassignedAccount"
                  class="app-modal-select"
                  @select="handleSourceAccountSelect"
                >
                  <template #noResult>
                    <span>{{ text.common.noOptions }}</span>
                  </template>
                  <template #noOptions>
                    <span>{{ text.common.noOptions }}</span>
                  </template>
                </multiselect>
              </div>

              <div class="app-modal-field">
                <span>{{ text.modal.fields.targetAccount }}</span>
                <multiselect
                  ref="targetSelect"
                  v-model="form.targetAccountOption"
                  :options="targetAccountOptions"
                  track-by="value"
                  label="label"
                  :multiple="false"
                  :close-on-select="true"
                  :clear-on-select="false"
                  :preserve-search="false"
                  :show-labels="false"
                  :searchable="true"
                  :allow-empty="false"
                  :max-height="220"
                  :placeholder="text.modal.placeholders.unassignedAccount"
                  class="app-modal-select"
                  @select="handleTargetAccountSelect"
                >
                  <template #noResult>
                    <span>{{ text.common.noOptions }}</span>
                  </template>
                  <template #noOptions>
                    <span>{{ text.common.noOptions }}</span>
                  </template>
                </multiselect>
              </div>

              <label class="app-modal-field">
                <span>{{ text.modal.fields.amount }}</span>
                <AppMoneyInput
                  v-model="form.amount"
                  output-mode="number"
                  :decimal-places="2"
                  :minimum-value="0"
                  :currency="activeCurrency"
                  :placeholder="text.modal.placeholders.amount"
                />
              </label>

              <label class="app-modal-field">
                <span>{{ text.modal.fields.date }}</span>
                <VueDatePicker
                  v-model="form.dateModel"
                  class="app-modal-datepicker"
                  menu-class-name="app-modal-datepicker-menu"
                  :enable-time-picker="false"
                  auto-apply
                  locale="es-MX"
                  format="dd/MM/yyyy"
                  hide-input-icon
                />
              </label>

              <label class="app-modal-field full-width">
                <span>{{ text.modal.fields.description }}</span>
                <input
                  v-model.trim="form.description"
                  type="text"
                  class="form-control"
                  maxlength="255"
                  autocomplete="off"
                  :placeholder="text.modal.placeholders.description"
                />
              </label>
            </div>
          </section>

          <footer class="app-modal__footer">
            <div class="app-modal__footer-right">
              <button type="button" class="btn btn-light" :disabled="loading" @click="emitClose">{{ text.actions.cancel }}</button>
              <button type="submit" class="btn btn-primary" :disabled="loading">
                <i v-if="loading" class="fa fa-circle-o-notch fa-spin" aria-hidden="true"></i>
                <span>{{ isEditMode ? text.actions.update : text.actions.create }}</span>
              </button>
            </div>
          </footer>
        </form>
      </div>
    </section>
  </div>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import AppMoneyInput from '@/components/common/AppMoneyInput.vue';
import { formatDateForApi, parsePresentationDate } from '@/utils/datePresentation';
import '@vuepic/vue-datepicker/dist/main.css';

const buildToday = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

export default {
  name: 'FinancialTransferModal',
  components: {
    VueDatePicker,
    AppMoneyInput,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    text: {
      type: Object,
      required: true,
    },
    accounts: {
      type: Array,
      default: () => [],
    },
    initialData: {
      type: Object,
      default: null,
    },
  },
  emits: ['close', 'submit'],
  data() {
    return {
      form: this.createInitialForm(),
    };
  },
  computed: {
    isEditMode() {
      return String(this.mode || 'create').trim().toLowerCase() === 'edit';
    },
    accountOptions() {
      const rows = Array.isArray(this.accounts) ? this.accounts : [];
      return rows
        .map((account) => {
          const id = Number(account?.id || 0);
          if (id <= 0) {
            return null;
          }

          const name = String(account?.name || '').trim();
          const currency = String(account?.currency || '').trim().toUpperCase();

          return {
            value: id,
            label: currency ? `${name} (${currency})` : name,
            meta: {
              currency,
            },
          };
        })
        .filter(Boolean);
    },
    sourceAccountOptions() {
      const targetId = Number(this.form?.targetAccountOption?.value || 0);
      return this.accountOptions.filter((option) => Number(option?.value || 0) !== targetId);
    },
    targetAccountOptions() {
      const sourceId = Number(this.form?.sourceAccountOption?.value || 0);
      return this.accountOptions.filter((option) => Number(option?.value || 0) !== sourceId);
    },
    activeCurrency() {
      const sourceCurrency = String(this.form?.sourceAccountOption?.meta?.currency || '').trim().toUpperCase();
      if (sourceCurrency !== '') {
        return sourceCurrency;
      }

      return 'MXN';
    },
  },
  watch: {
    show(nextValue) {
      if (!nextValue) {
        return;
      }

      this.hydrateForm();
    },
    initialData: {
      deep: true,
      handler() {
        if (this.show) {
          this.hydrateForm();
        }
      },
    },
    accountOptions() {
      if (this.show) {
        this.hydrateForm();
      }
    },
  },
  methods: {
    createInitialForm() {
      return {
        dateModel: parsePresentationDate(buildToday()),
        sourceAccountOption: null,
        targetAccountOption: null,
        description: '',
        amount: 0,
      };
    },
    resolveOption(accountId) {
      const normalized = Number(accountId || 0);
      if (normalized <= 0) {
        return null;
      }

      return this.accountOptions.find((option) => Number(option?.value || 0) === normalized)
        || { value: normalized, label: String(normalized) };
    },
    resolveDateModel(value) {
      const parsed = parsePresentationDate(value);
      if (parsed) {
        return parsed;
      }

      return parsePresentationDate(buildToday());
    },
    resolvePayloadDate(dateModel) {
      const normalized = parsePresentationDate(dateModel);
      if (!normalized) {
        return buildToday();
      }

      return formatDateForApi(normalized, { allowString: true }) || buildToday();
    },
    hydrateForm() {
      const payload = this.initialData && typeof this.initialData === 'object'
        ? this.initialData
        : null;

      if (!payload) {
        this.form = this.createInitialForm();
        return;
      }

      this.form = {
        dateModel: this.resolveDateModel(payload?.date || buildToday()),
        sourceAccountOption: this.resolveOption(payload?.financial_account_source_id),
        targetAccountOption: this.resolveOption(payload?.financial_account_target_id),
        description: String(payload?.description || '').trim(),
        amount: Number(payload?.source_amount || payload?.target_amount || payload?.amount || 0) || 0,
      };
    },
    emitClose() {
      if (this.loading) {
        return;
      }

      this.$emit('close');
    },
    submitForm() {
      const sourceAccountId = Number(this.form.sourceAccountOption?.value || 0);
      const targetAccountId = Number(this.form.targetAccountOption?.value || 0);
      const amount = Number(this.form.amount || 0);

      this.$emit('submit', {
        date: this.resolvePayloadDate(this.form.dateModel),
        financial_account_source_id: sourceAccountId,
        financial_account_target_id: targetAccountId,
        description: String(this.form.description || '').trim(),
        amount,
        source_amount: amount,
        target_amount: amount,
        exchange_rate: 1,
      });
    },
    handleSourceAccountSelect(option) {
      const selectedId = Number(option?.value || 0);
      const targetId = Number(this.form?.targetAccountOption?.value || 0);

      if (selectedId > 0 && targetId === selectedId) {
        this.form.targetAccountOption = null;
      }

      this.$nextTick(() => {
        this.$refs?.sourceSelect?.deactivate?.();
      });
    },
    handleTargetAccountSelect(option) {
      const selectedId = Number(option?.value || 0);
      const sourceId = Number(this.form?.sourceAccountOption?.value || 0);

      if (selectedId > 0 && sourceId === selectedId) {
        this.form.sourceAccountOption = null;
      }

      this.$nextTick(() => {
        this.$refs?.targetSelect?.deactivate?.();
      });
    },
  },
};
</script>

<style scoped>
.financial-transfer-modal {
  max-width: 700px;
  height: auto;
  max-height: calc(100vh - 48px);
}

.financial-transfer-modal__content {
  min-height: auto;
  overflow: visible;
}

.app-modal-select {
  width: 100%;
}
</style>
